import React, { useMemo, useState } from "react";
import { FileBarChart, Printer } from "lucide-react";
import { Btn, Modal } from "./erp-shared";

// ===== Constants =====
const HIJRI_MONTHS = [
  "محرم", "صفر", "ربيع الأول", "ربيع الآخر", "جمادى الأولى", "جمادى الآخرة",
  "رجب", "شعبان", "رمضان", "شوال", "ذو القعدة", "ذو الحجة",
];
const AR_GREGORIAN_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
const ORG_NAME = "جمعية الدعوة والإرشاد وتوعية الجاليات بشمال بريدة";

// ===== Hijri helpers =====
// Read the Umm al-Qura (Islamic) calendar parts of a Gregorian Date.
function hijriPartsOf(date) {
  const parts = new Intl.DateTimeFormat("en-US-u-ca-islamic-umalqura", {
    day: "numeric", month: "numeric", year: "numeric",
  }).formatToParts(date);
  let y = 0, mo = 0, d = 0;
  for (const p of parts) {
    if (p.type === "year") y = parseInt(p.value, 10);
    else if (p.type === "month") mo = parseInt(p.value, 10);
    else if (p.type === "day") d = parseInt(p.value, 10);
  }
  return { y, mo, d };
}

// Build a Gregorian Date at local noon (avoids DST / timezone boundary drift).
function gregAtNoon(off) {
  return new Date(1900, 0, 1 + off, 12, 0, 0, 0);
}

// Convert a Hijri (Umm al-Qura) date to a Gregorian Date via binary search
// over the Gregorian day range, comparing through Intl. No external library.
function hijriToGregorian(hy, hm, hd) {
  if (hm < 1 || hm > 12 || hd < 1 || hd > 30) return null;
  const target = hy * 10000 + hm * 100 + hd;
  const ord = (off) => {
    const { y, mo, d } = hijriPartsOf(gregAtNoon(off));
    return y * 10000 + mo * 100 + d;
  };
  let lo = 0;                                       // 1900-01-01
  let hi = Math.round((2200 - 1900) * 365.25);      // ~ up to year 2200
  if (target < ord(lo) || target > ord(hi)) return null;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ord(mid) < target) lo = mid + 1;
    else hi = mid;
  }
  const result = gregAtNoon(lo);
  const { y, mo, d } = hijriPartsOf(result);
  if (y * 10000 + mo * 100 + d !== target) return null; // day does not exist
  return result;
}

// ===== normalizeDate =====
// Accepts every date shape used across the project and returns a Gregorian
// Date | null. Never throws.
function buildGregorian(y, mo, d) {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d, 12, 0, 0, 0);
  if (isNaN(dt.getTime())) return null;
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

export function normalizeDate(value) {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,4})[/-](\d{1,2})[/-](\d{1,4})$/);
  if (!m) return null;
  const a = parseInt(m[1], 10), b = parseInt(m[2], 10), c = parseInt(m[3], 10);

  // First part is the year (4 digits, or clearly out of day range)
  if (m[1].length === 4 || a > 31) {
    if (a >= 1300 && a <= 1500) return hijriToGregorian(a, b, c);
    return buildGregorian(a, b, c);
  }
  // Last part is the year → day-first (DMY)
  if (m[3].length === 4 || c > 31) {
    if (c >= 1300 && c <= 1500) return hijriToGregorian(c, b, a);
    return buildGregorian(c, b, a);
  }
  return null;
}

// ===== matchesMonth =====
// `hijriYM` is a Hijri "YYYY-MM" (e.g. "1447-09"). Returns true when `value`
// falls within that Hijri month. Hijri values are compared textually (no Intl
// conversion); Gregorian values are converted to Hijri before comparing.
// Empty / unparseable values → false (never throws).
export function matchesMonth(value, hijriYM) {
  const [ty, tm] = String(hijriYM).split("-").map(Number);
  if (!ty || !tm) return false;
  if (value == null) return false;
  const s = String(value).trim();
  if (!s) return false;

  // Hijri value: YYYY/MM or YYYY/MM/DD (day optional), year 1300–1500.
  // Compare the year/month parts directly — no conversion needed.
  const hm = s.match(/^(\d{4})[/-](\d{1,2})(?:[/-](\d{1,2}))?$/);
  if (hm) {
    const y = parseInt(hm[1], 10);
    const mo = parseInt(hm[2], 10);
    if (y >= 1300 && y <= 1500) return y === ty && mo === tm;
  }

  // Otherwise treat as Gregorian: normalize, convert to Hijri, then compare.
  const d = normalizeDate(value);
  if (!d) return false;
  const { y, mo } = hijriPartsOf(d);
  return y === ty && mo === tm;
}

// ===== hijriLabel =====
// Describes the Hijri month(s) overlapping a Gregorian "YYYY-MM".
export function hijriLabel(ym) {
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return "";
  const first = new Date(y, m - 1, 1, 12);
  const last = new Date(y, m, 0, 12); // day 0 of next month = last day of this month
  const a = hijriPartsOf(first);
  const b = hijriPartsOf(last);
  const nameA = HIJRI_MONTHS[a.mo - 1] || "";
  const nameB = HIJRI_MONTHS[b.mo - 1] || "";
  if (a.y === b.y && a.mo === b.mo) return `${nameA} ${a.y}هـ`;
  if (a.y === b.y) return `${nameA} – ${nameB} ${b.y}هـ`;
  return `${nameA} ${a.y}هـ – ${nameB} ${b.y}هـ`;
}

// ===== Month list =====
// Describe the Gregorian month(s) overlapping a Hijri year/month — the mirror
// of hijriLabel. Returned without surrounding parentheses.
function gregLabelForHijri(hy, hm) {
  const start = hijriToGregorian(hy, hm, 1);
  let ny = hy, nm = hm + 1;
  if (nm === 13) { nm = 1; ny += 1; }
  const nextStart = hijriToGregorian(ny, nm, 1);
  if (!start || !nextStart) return "";
  const end = new Date(nextStart.getTime() - 24 * 3600 * 1000);
  const sM = start.getMonth(), sY = start.getFullYear();
  const eM = end.getMonth(), eY = end.getFullYear();
  const sName = AR_GREGORIAN_MONTHS[sM] || "";
  const eName = AR_GREGORIAN_MONTHS[eM] || "";
  if (sY === eY && sM === eM) return `${sName} ${sY}`;
  if (sY === eY) return `${sName} – ${eName} ${eY}`;
  return `${sName} ${sY} – ${eName} ${eY}`;
}

// Last `n` Hijri months, newest first, starting from the current Hijri month.
function lastHijriMonths(n) {
  const now = hijriPartsOf(new Date());
  let y = now.y, m = now.mo;
  const out = [];
  for (let i = 0; i < n; i++) {
    const ym = `${y}-${String(m).padStart(2, "0")}`;
    out.push({ ym, label: `${HIJRI_MONTHS[m - 1]} ${y}هـ`, greg: gregLabelForHijri(y, m) });
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}

// ===== Cell formatting =====
function cellValue(col, row) {
  const raw = row[col.key];
  if (typeof col.format === "function") {
    try { return col.format(raw, row); } catch { return "—"; }
  }
  return raw == null || String(raw).trim() === "" ? "—" : String(raw);
}

// ===== printMonthlyReport =====
export function printMonthlyReport(payload) {
  const {
    moduleTitle = "",
    monthLabel = "",
    gregText = "",
    orgName = ORG_NAME,
    kpis = [],
    monthlySources = [],
    cumulativeSources = [],
    budget,
  } = payload || {};

  const w = window.open("", "_blank");
  if (!w) { alert("الرجاء السماح بالنوافذ المنبثقة لعرض التقرير"); return; }

  const esc = (v) => String(v ?? "—").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

  const kpiHtml = kpis.length
    ? `<section class="block"><h2>المؤشرات الرئيسية</h2><div class="kpis">${
        kpis.map((k) => `<div class="kpi"><div class="kpi-v">${esc(k.value)}</div><div class="kpi-l">${esc(k.label)}</div></div>`).join("")
      }</div></section>`
    : "";

  const tableHtml = (src) => {
    const cols = src.columns || [];
    const head = `<tr>${cols.map((c) => `<th>${esc(c.label)}</th>`).join("")}</tr>`;
    const body = (src.rows || []).length
      ? src.rows.map((row) => `<tr>${cols.map((c) => `<td>${esc(cellValue(c, row))}</td>`).join("")}</tr>`).join("")
      : `<tr><td class="empty" colspan="${cols.length || 1}">لا توجد سجلات</td></tr>`;
    return `<section class="block"><h2>${esc(src.label)} <span class="count">(${(src.rows || []).length})</span></h2>`
      + `<table><thead>${head}</thead><tbody>${body}</tbody></table></section>`;
  };

  const monthlyHtml = monthlySources.map(tableHtml).join("");

  const cumulativeHtml = cumulativeSources.length
    ? `<section class="block"><h2>إجماليات تراكمية</h2><div class="kpis">${
        cumulativeSources.map((s) => `<div class="kpi"><div class="kpi-v">${(s.rows || []).length}</div><div class="kpi-l">${esc(s.label)}</div></div>`).join("")
      }</div></section>`
    : "";

  const budgetHtml = (typeof budget === "number" && !isNaN(budget))
    ? `<section class="block"><h2>ملخص مالي</h2><div class="budget"><span>الميزانية المعتمدة</span><strong>${budget.toLocaleString("ar-SA")} ر.س</strong></div></section>`
    : "";

  const issueDate = new Date().toLocaleDateString("ar-SA");

  const html = `<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8">
<title>${esc(moduleTitle)} — ${esc(monthLabel)}</title>
<link href="https://fonts.googleapis.com/css2?family=Readex+Pro:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Readex Pro', sans-serif; color: #003d58; margin: 0; background: #F7F8FA; }
  .page { max-width: 820px; margin: 0 auto; padding: 40px 44px; background: #fff; box-shadow: 0 1px 3px rgba(0,61,88,.08); }
  .head { border-bottom: 3px solid #dcc160; padding-bottom: 16px; margin-bottom: 26px; }
  .head .org { font-size: 12px; color: #5a7b8f; }
  .head .dept { font-size: 20px; font-weight: 700; color: #003d58; margin-top: 6px; }
  .head .meta { font-size: 12px; color: #5a7b8f; margin-top: 8px; display: flex; gap: 18px; flex-wrap: wrap; }
  .head .meta b { color: #003d58; font-weight: 600; }
  .block { margin-bottom: 26px; page-break-inside: avoid; }
  .block h2 { font-size: 15px; font-weight: 700; color: #003d58; margin: 0 0 12px; padding-bottom: 8px; border-bottom: 2px solid #dcc160; }
  .block h2 .count { font-size: 12px; color: #5a7b8f; font-weight: 500; }
  .kpis { display: flex; flex-wrap: wrap; gap: 12px; }
  .kpi { flex: 1; min-width: 140px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; background: #fbfcfd; }
  .kpi-v { font-size: 22px; font-weight: 700; color: #00b4a2; }
  .kpi-l { font-size: 12px; color: #5a7b8f; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  thead th { background: #00b4a2; color: #fff; font-weight: 600; padding: 9px 12px; text-align: right; }
  tbody td { border: 1px solid #e2e8f0; padding: 8px 12px; color: #003d58; }
  tbody tr:nth-child(even) td { background: #f7fafb; }
  td.empty { text-align: center; color: #9fb3c0; }
  .budget { display: flex; justify-content: space-between; align-items: center; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 18px; background: #fbfcfd; font-size: 14px; }
  .budget strong { color: #003d58; font-size: 16px; }
  .foot { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 14px; font-size: 10.5px; color: #5a7b8f; display: flex; justify-content: space-between; }
  @media print {
    body { background: #fff; }
    .page { max-width: none; margin: 0; padding: 0; box-shadow: none; }
    .block { page-break-inside: avoid; }
    table, tr, td, th { page-break-inside: avoid; }
  }
</style></head><body>
<div class="page">
  <div class="head">
    <div class="org">${esc(orgName)}</div>
    <div class="dept">التقرير المجمّع الشهري — ${esc(moduleTitle)}</div>
    <div class="meta">
      <span><b>الشهر:</b> ${esc(monthLabel)}${gregText ? ` (${esc(gregText)})` : ""}</span>
      <span><b>تاريخ الإصدار:</b> ${esc(issueDate)}</span>
    </div>
  </div>
  ${kpiHtml}
  ${monthlyHtml}
  ${cumulativeHtml}
  ${budgetHtml}
  <div class="foot"><span>${esc(orgName)}</span><span>توقيع المسؤول: ______________</span></div>
</div>
</body></html>`;

  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}

// ===== MonthlyReportButton =====
export function MonthlyReportButton({ moduleTitle = "", sources = [], kpis, budget }) {
  const [open, setOpen] = useState(false);
  const months = useMemo(() => lastHijriMonths(18), []);
  const [ym, setYm] = useState(months[0]?.ym || "");

  const selected = months.find((mm) => mm.ym === ym) || months[0];

  // Apply monthly filtering / cumulative passthrough for the current selection.
  const filtered = useMemo(() => {
    return sources.map((src) => {
      if (src.mode === "cumulative") {
        const rows = src.rows || [];
        return { ...src, rows, count: rows.length };
      }
      const rows = (src.rows || []).filter((r) => matchesMonth(r[src.dateField], ym));
      return { ...src, rows, count: rows.length };
    });
  }, [sources, ym]);

  const monthlyFiltered = filtered.filter((s) => s.mode !== "cumulative");
  const cumulativeFiltered = filtered.filter((s) => s.mode === "cumulative");
  const monthlyTotal = monthlyFiltered.reduce((acc, s) => acc + s.count, 0);
  const hasData = monthlyTotal > 0;

  const build = () => {
    const sourcesMap = {};
    filtered.forEach((s) => { sourcesMap[s.label] = s.rows; });
    const kpiList = typeof kpis === "function" ? (kpis(sourcesMap) || []) : [];
    printMonthlyReport({
      moduleTitle,
      monthLabel: selected?.label || "",
      gregText: selected?.greg || "",
      kpis: kpiList,
      monthlySources: monthlyFiltered.map((s) => ({ label: s.label, columns: s.columns || [], rows: s.rows })),
      cumulativeSources: cumulativeFiltered.map((s) => ({ label: s.label, rows: s.rows })),
      budget,
    });
  };

  return (
    <>
      <Btn variant="primary" icon={FileBarChart} onClick={() => setOpen(true)}>
        تقرير مجمّع
      </Btn>

      {open && (
        <Modal title="التقرير المجمّع الشهري" onClose={() => setOpen(false)} width={560}>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#5a7b8f", margin: "0 0 10px" }}>
            اختر الشهر
          </p>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 0,
          }}>
            {months.map((mm) => {
              const isSel = mm.ym === ym;
              return (
                <button
                  key={mm.ym}
                  type="button"
                  onClick={() => setYm(mm.ym)}
                  style={{
                    padding: "12px 8px",
                    borderRadius: 10,
                    border: isSel ? "none" : "1px solid #e2e8f0",
                    background: isSel ? "#00b4a2" : "#fafbfc",
                    color: isSel ? "#ffffff" : "#003d58",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 500,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (mm.ym !== ym) {
                      e.currentTarget.style.borderColor = "#8fded6";
                      e.currentTarget.style.background = "#f7fbfa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mm.ym !== ym) {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.background = "#fafbfc";
                    }
                  }}
                >
                  {mm.label}
                </button>
              );
            })}
          </div>

          {selected?.greg && (
            <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: "#5a7b8f", textAlign: "center", fontWeight: 500 }}>
              الموافق {selected.greg}
            </p>
          )}

          <div style={{ marginTop: 4, marginBottom: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#5a7b8f", margin: "0 0 10px" }}>
              معاينة السجلات المطابقة
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {monthlyFiltered.map((s) => (
                <div key={s.label} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  border: "1px solid #ebebeb", borderRadius: 10, padding: "10px 14px",
                }}>
                  <span style={{ fontSize: 13, color: "#003d58" }}>{s.label}</span>
                  <span style={{
                    fontSize: 13, fontWeight: 700,
                    color: s.count > 0 ? "#00b4a2" : "#9fb3c0",
                  }}>{s.count}</span>
                </div>
              ))}
              {cumulativeFiltered.map((s) => (
                <div key={s.label} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  border: "1px dashed #ebebeb", borderRadius: 10, padding: "10px 14px",
                }}>
                  <span style={{ fontSize: 13, color: "#5a7b8f" }}>{s.label} (تراكمي)</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#5a7b8f" }}>{s.count}</span>
                </div>
              ))}
            </div>

            {!hasData && (
              <p style={{
                marginTop: 14, padding: "10px 14px", borderRadius: 10,
                background: "#fdf6e3", color: "#8a6d1f", fontSize: 13, textAlign: "center",
              }}>
                لا توجد بيانات لهذا الشهر
              </p>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Btn variant="ghost" onClick={() => setOpen(false)}>إلغاء</Btn>
            <Btn variant="teal" icon={Printer} disabled={!hasData} onClick={build}>
              عرض وطباعة
            </Btn>
          </div>
        </Modal>
      )}
    </>
  );
}
